// @ts-nocheck
import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  FormLabel,
  Button,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDialog, useIfMounted, useOnMount } from '../../../../common/hooks';
import ValidationUtils from '../../../../common/utils/ValidationUtils';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
    '& .MuiFormGroup-root': {
      minHeight: '78px',
      paddingTop: '4px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  switch: {
    margin: '-4px 0 0 0',
    alignItems: 'flex-start',
    '& .MuiSwitch-root': {
      marginLeft: '-8px',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function CampaignForm(props) {
  const {
    campaign,
    products,
    onSave,
    onCancel,
    editMode,
    isActive,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
    onCloseCampaign,
  } = props;
  const { showDialog } = useDialog();
  const ifMounted = useIfMounted();

  function doFilterProductList(flowType) {
    if (flowType === 'B2B') {
      return products.filter(
        product => product.id === 1260 || product.id === 1134 || product.id === 1263
      );
    }

    return products.filter(product => product.id !== 1260);
  };

  const [filteredProducts, setFilteredProducts] = useState(doFilterProductList('OPTOUT'));
  const [product, setProduct] = useState(_.defaultTo(products.find(x => x.id === campaign?.productId), _.defaultTo(filteredProducts[0], products[0])));
  const [editable, setEditable] = useState(!editMode);
  const hasSeries = campaign?.series.length > 0;
  const disabled = !editable || hasSeries;
  const [t] = useTranslation();
  const { control, errors, formState, reset, register, handleSubmit, setValue, getValues, clearErrors } =
    useForm();
  const classes = useStyles();
  const { isDirty, isSubmitting } = formState;
  const [discountType, setDiscountType] = useState(_.defaultTo(campaign?.discountType, 'PERCENT'));
  const discountValue = campaign?.discountValue ?
    (discountType === 'AMOUNT' ? 
      _.defaultTo(((campaign.discountValue) / 100).toFixed(2).replace('.', ',')) :
      campaign.discountValue) :
    '';

    const handleDiscountTypeChange = (event) => {
      setDiscountType(event.target.value);
    };

  const formatDiscountValue = () => {
    return discountType === 'PERCENT' ?
     '%' :
     'Kr.';
  };

  const isDiscountDisabled = () => {
    return disabled || getValues('flowType') !== 'DISCOUNT';
  };

  useOnMount(() => {
    if(campaign)
      setFilteredProducts(doFilterProductList(campaign.flowType));
  });

  const onSubmit = async data => {
    if(campaign && hasSeries) {
      data.discountType = campaign.discountType;
      data.discountValue = campaign.discountValue;
      data.discountDuration = campaign.discountDuration;
      data.discountDays = campaign.discountDays;
      data.productId = campaign.productId;
      data.flowType = campaign.flowType;
      data.autoCancel = campaign.autoCancel;
      data.type = campaign.type;
    } else {
      if (data.flowType !== 'DISCOUNT') {
        data.discountType = 'NONE';
        data.discountValue = 0;
        data.discountDuration = 0;
        data.discountDays = _.toNumber(data.discountDays);
      } else {
        data.discountValue = _.toNumber(data.discountValue.replace(',', '')) ?? 0;
        data.discountDuration = _.toNumber(data.discountDuration);
        data.discountDays = 0;
      }
    }

    try {
      await onSave(data);
      ifMounted(() => {
        reset(data);
        if (editMode) {
          setEditable(false);
        }
      });
    } catch (e) {
      // Error already handled
    }
  };

  const validateDiscountDays = value => {
    if (value === '0' && ['B2B', 'DISCOUNT'].indexOf(getValues('flowType')) === -1) {
      return t('campaign.campaignValidDaysB2B');
    }
    return true;
  };

  const validateDiscountFormat = () => {
    return discountType === 'PERCENT' ? {
      value: /^(100|[1-9]?[0-9])$/,
      message: t('campaign.campaignDiscountValuePercent'),
    } :
    {
      value: /^[0-9]*[0-9][,][0-9]{2}$/,
      message: t('campaign.campaignDiscountValueDecimal'),
    }
  };

  const validateDiscountDuration = value => {
    return ValidationUtils.validateDiscountDuration(value, t);
  };

  const validateDiscountValue = value => {
    if(getValues('flowType') === 'DISCOUNT') {
      if (value === '0' || value === '') {
        return t('campaign.campaignValidDiscountValue');
      } else if (discountType === 'AMOUNT' && (_.toNumber(value.replace(',', '.')) ?? 0) * 100 > product.price) {
        return `${t('campaign.campaignValidDiscountValueTooHigh')} ${product.price / 100},00 Kr.`;
      }
    }
    return true;
  };

  const handleCancel = () => {
    if (editMode && editable) {
      reset();
      setEditable(false);
    } else {
      onCancel();
    }
  };

  const handleFlowTypeChange = event => {
    setFilteredProducts(doFilterProductList(event.target.value));
    if(event.target.value !== 'DISCOUNT')
      clearErrors(['discountDuration', 'discountValue']);
  };

  const handleProductChange = event => {
    const newProduct = filteredProducts.find(x => x.id == event.target.value);
    setProduct(newProduct);
  }

  const handleShowCloseCampaignDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('campaign.campaignCloseConfirm')}</Typography>,
      onConfirm: onCloseCampaign,
    });
  };

  return (
    <Box id="CampaignForm-container" className={classes.container}>
      <form id="CampaignForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <TextField
                id="CampaignForm-title"
                name="title"
                label={t('campaign.campaignTitle')}
                variant="outlined"
                defaultValue={campaign?.title}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('campaign.campaignTitleRequired'),
                })}
                error={!!errors.title}
                helperText={errors.title?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="productId"
                control={control}
                defaultValue={
                  _.defaultTo(campaign?.productId, '') || _.defaultTo(filteredProducts[0]?.id, '')
                }
                render={({ onChange, value }) => (
                  <TextField
                    id="CampaignForm-productId"
                    label={t('campaign.campaignProductId')}
                    variant="outlined"
                    disabled={disabled}
                    fullWidth
                    select
                    value={value}
                    onChange={(e) => { handleProductChange(e); onChange(e); }}
                  >
                    {filteredProducts.map(product => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.title}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="CampaignForm-discountDays"
                name="discountDays"
                label={t('campaign.campaignValidDays')}
                variant="outlined"
                defaultValue={campaign?.discountDays}
                InputLabelProps={{ shrink: editMode }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">dage</InputAdornment>,
                }}
                inputRef={isDiscountDisabled() && register(!disabled && {
                  required: t('campaign.campaignValidDaysRequired'),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t('campaign.campaignValidDaysInteger'),
                  },
                  validate: validateDiscountDays,
                })}
                error={!!errors.discountDays}
                helperText={errors.discountDays?.message}
                disabled={disabled || !isDiscountDisabled()}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
            <TextField
                id="CampaignForm-discountDuration"
                name="discountDuration"
                label={t('campaign.campaignDiscountDuration')}
                variant="outlined"
                defaultValue={campaign?.discountDuration ?? ''}
                InputLabelProps={{ shrink: editMode }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">dage</InputAdornment>,
                }}
                inputRef={register(!isDiscountDisabled() && {
                  required: t('campaign.campaignDiscountDurationRequired'),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t('campaign.campaignValidDaysInteger'),
                  },
                  validate: validateDiscountDuration,
                })}
                error={!!errors.discountDuration}
                helperText={errors.discountDuration?.message}
                disabled={isDiscountDisabled()}
                fullWidth
            />
          </Grid>
            <Grid item xs={3}>
              <TextField
                id="CampaignForm-discountValue"
                name="discountValue"
                label={t('campaign.campaignDiscountValue')}
                variant="outlined"
                defaultValue={discountValue}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{formatDiscountValue()}</InputAdornment>,
                }}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register(!isDiscountDisabled() && {
                  required: t('campaign.campaignDiscountValueRequired'),
                  pattern: validateDiscountFormat(),
                  validate: validateDiscountValue,
                })}
                error={!!errors.discountValue}
                helperText={errors.discountValue?.message}
                disabled={isDiscountDisabled()}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('campaign.campaignDiscountType')}</FormLabel>
                <Controller
                    name="discountType"
                    control={control}
                    defaultValue={discountType}
                    render={({ onChange, value }) => (
                      <RadioGroup id="CampaignForm-discountType"
                        value={value}
                        onChange={(e) => { handleDiscountTypeChange(e); onChange(e); }}
                      >
                        <FormControlLabel
                            value="PERCENT"
                            control={<Radio />}
                            label={t('campaign.campaignDiscountTypePercent')}
                            disabled={isDiscountDisabled()}
                        />
                        <FormControlLabel
                            value="AMOUNT"
                            control={<Radio />}
                            label={t('campaign.campaignDiscountTypeAmount')}
                            disabled={isDiscountDisabled()}
                        />
                      </RadioGroup>
                    )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                label={t('campaign.campaignAutoCancel')}
                labelPlacement="top"
                className={classes.switch}
                control={
                  <Controller
                    name="autoCancel"
                    control={control}
                    defaultValue={_.defaultTo(campaign?.autoCancel, false)}
                    render={params => (
                      <Switch
                        id="CampaignForm-autoCancel"
                        checked={params.value}
                        onChange={e => params.onChange(e.target.checked)}
                        disabled={disabled}
                      />
                    )}
                  />
                }
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('campaign.campaignType')}</FormLabel>
                <Controller
                  name="type"
                  control={control}
                  defaultValue={_.defaultTo(campaign?.type, 'SINGLE')}
                  as={
                    <RadioGroup id="CampaignForm-type">
                      <FormControlLabel
                        value="SINGLE"
                        control={<Radio />}
                        label={t('campaign.campaignTypeSingle')}
                        disabled={disabled}
                      />
                      <FormControlLabel
                        value="MULTI"
                        control={<Radio />}
                        label={t('campaign.campaignTypeMulti')}
                        disabled={disabled}
                      />
                    </RadioGroup>
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('campaign.campaignFlowType')}</FormLabel>
                <Controller
                  name="flowType"
                  control={control}
                  defaultValue={_.defaultTo(campaign?.flowType, 'OPTOUT')}
                  render={({ onChange, value }) => (
                    <RadioGroup id="CampaignForm-flowType"
                      value={value}
                      onChange={(e) => { handleFlowTypeChange(e); onChange(e); }}
                    >
                      <FormControlLabel
                        value="DISCOUNT"
                        control={<Radio />}
                        label={t('campaign.campaignFlowTypeDiscount')}
                        disabled={disabled}
                      />
                      <FormControlLabel
                        value="OPTIN"
                        control={<Radio />}
                        label={t('campaign.campaignFlowTypeOptIn')}
                        disabled={disabled}
                      />
                      <FormControlLabel
                        value="OPTOUT"
                        control={<Radio />}
                        label={t('campaign.campaignFlowTypeOptOut')}
                        disabled={disabled}
                      />
                      {campaign && (
                        <>
                          <FormControlLabel
                            value="EXTENDED_ACCESS"
                            control={<Radio />}
                            label={t('campaign.campaignFlowTypeExtendedAccess')}
                            disabled
                          />
                          <FormControlLabel
                            value="DISCOUNTED_UPGRADE"
                            control={<Radio />}
                            label={t('campaign.campaignFlowTypeDiscountedUpgrade')}
                            disabled
                          />
                        </>
                      )}
                      <FormControlLabel
                        value="B2B"
                        control={<Radio />}
                        label={t('campaign.campaignFlowTypeB2B')}
                        disabled={disabled}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="CampaignForm-description"
                name="description"
                label={t('campaign.campaignDescription')}
                variant="outlined"
                defaultValue={campaign?.description}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register}
                disabled={!editable}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="CampaignForm-cancel"
            className={classes.button}
            startIcon={<Icon>{!editable ? cancelButtonIcon : 'cancel'}</Icon>}
            onClick={handleCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {!editable ? cancelButtonText : t('common.cancel')}
            </Typography>
          </Button>
          {isActive && (
            <Button
              id="CampaignForm-close"
              className={classes.button}
              color="primary"
              startIcon={<Icon>lock</Icon>}
              onClick={handleShowCloseCampaignDialog}
              disabled={actionsDisabled}
            >
              <Typography variant="button" noWrap>
                {t('campaign.campaignClose')}
              </Typography>
            </Button>
          )}
          {!editable ? (
            <Button
              id="CampaignForm-edit"
              key="edit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>edit</Icon>}
              onClick={() => setEditable(true)}
              disabled={actionsDisabled || isSubmitting}
            >
              <Typography variant="button" noWrap>
                {t('common.edit')}
              </Typography>
            </Button>
          ) : (
            <Button
              id="CampaignForm-save"
              key="save"
              type="submit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
              disabled={actionsDisabled || isSubmitting || !isDirty}
            >
              <Typography variant="button" noWrap>
                {_.defaultTo(saveButtonText, t('common.save'))}
              </Typography>
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
}

CampaignForm.defaultProps = {
  campaign: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

CampaignForm.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    type: PropTypes.string,
    created: PropTypes.string,
    discountDays: PropTypes.number,
    discountDuration: PropTypes.number,
    discountType: PropTypes.string,
    discountValue: PropTypes.number,
    autoCancel: PropTypes.bool,
    productId: PropTypes.number,
    flowType: PropTypes.string,
    productTitle: PropTypes.string,
    description: PropTypes.string,
  }),
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCloseCampaign: PropTypes.func,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default CampaignForm;
